import React, { FC, useState, useMemo } from 'react'

import { EuiPanel, EuiTitle, EuiText, EuiLink, EuiSpacer, EuiFlyoutBody, EuiFlyoutHeader, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { AcDomain } from 'api/interfaces/AcDomain'
import { AIPlan } from 'api/interfaces/AIPlan'
import { useGetDomainsQuery } from 'api/rtkQueryApi/platform/domainsApi'
import { defaultLogoStyle, defaultLogoStyleSquareImg } from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/logoUtils'

export interface AIPlanTopInternetDisplaySitesProps {
  aiPlan: AIPlan
  internetDisplayRef?: React.RefObject<HTMLDivElement>
}

const AIPlanTopInternetDisplaySites: FC<AIPlanTopInternetDisplaySitesProps> = ({ aiPlan, internetDisplayRef }) => {
  const { data: domains, isLoading: isDomainsLoading, isError: isDomainsError } = useGetDomainsQuery(undefined, { skip: !aiPlan })
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false)

  const highlightedDomains = useMemo(() => {
    if (!domains || domains.length <= 0) {
      return []
    }

    const domainSet = new Set(aiPlan?.highlightedDomainIds || [])
    return domains.filter(d => domainSet.has(d.id))
  }, [domains, aiPlan])

  const sortedDomains = useMemo(() => {
    if (!domains) {
      return []
    }

    return [...domains].sort((a, b) => a.name.localeCompare(b.name))
  }, [domains])

  const ListItem = ({ domain, index, showNumbers }: { domain: AcDomain; index: number; showNumbers: boolean }) => {
    const logoStyle = { ...(domain.isSquare ? defaultLogoStyleSquareImg : defaultLogoStyle) }
    if (domain.maxWidth) {
      logoStyle.maxWidth = domain.maxWidth
    }

    if (domain.maxHeight) {
      logoStyle.maxHeight = domain.maxHeight
    }

    if (domain.domain === 'allure.com' || domain.domain === 'brakeforit.com' || domain.domain === 'buzzworthy.com') {
      logoStyle.backgroundColor = 'black'
      logoStyle.padding = '1px'
    }

    const finalLogoStyle = { ...logoStyle }

    return (
      <li
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '12px',
          padding: '8px 0',
          height: '80px'
        }}>
        {showNumbers && <EuiText style={{ display: 'flex', alignItems: 'center', marginRight: '12px', marginBottom: 'auto' }}>{index + 1}.</EuiText>}
        {!showNumbers && <EuiText style={{ fontSize: '18px', display: 'flex', alignItems: 'center', marginRight: '12px', marginBottom: 'auto' }}>{'>'}</EuiText>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: 'auto' }}>
          <img src={domain.logoUrl} alt={domain.name} style={finalLogoStyle} crossOrigin='anonymous' />
          <EuiText size='s'>{domain.domain}</EuiText>
        </div>
      </li>
    )
  }

  if (isDomainsLoading) {
    return (
      <EuiPanel paddingSize='m' hasShadow>
        <EuiFlexGroup alignItems='center' responsive={false}>
          <EuiFlexItem />
          <EuiFlexItem grow={false}>
            <EuiTitle size='s'>
              <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>Internet Ad Placements</h3>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem />
        </EuiFlexGroup>
        <EuiText textAlign='center'>Loading domains...</EuiText>
      </EuiPanel>
    )
  }

  if (isDomainsError || !domains || domains.length === 0) {
    return (
      <EuiPanel paddingSize='m' hasShadow>
        <EuiFlexGroup alignItems='center' responsive={false}>
          <EuiFlexItem />
          <EuiFlexItem grow={false}>
            <EuiTitle size='s'>
              <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>Internet Ad Placements</h3>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem />
        </EuiFlexGroup>
        <EuiText textAlign='center'>No domains available</EuiText>
      </EuiPanel>
    )
  }

  return (
    <EuiPanel paddingSize='m' hasShadow>
      <EuiFlexGroup alignItems='center' responsive={false}>
        <EuiFlexItem />
        <EuiFlexItem grow={false}>
          <EuiTitle size='s'>
            <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>Internet Ad Placements</h3>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem />
      </EuiFlexGroup>
      <EuiText size='s' style={{ whiteSpace: 'pre-wrap' }}>
        Internet ads will be placed across any of the following sites, optimized for audience match.
      </EuiText>

      <EuiSpacer size='xl' />

      <div
        ref={internetDisplayRef}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0 10px',
          marginLeft: '2.5rem'
        }}>
        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {highlightedDomains.slice(0, 5).map((domain, index) => {
            return <ListItem domain={domain} index={index} key={index} showNumbers={true} />
          })}
        </ol>

        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {highlightedDomains.slice(5, 10).map((domain, index) => {
            return <ListItem domain={domain} index={index + 5} key={index + 5} showNumbers={true} />
          })}
        </ol>
      </div>

      <EuiSpacer size='m' />

      <div style={{ textAlign: 'center' }}>
        <EuiLink onClick={() => setIsFlyoutVisible(true)}>See full list</EuiLink>
      </div>

      {isFlyoutVisible && (
        <AcFlyout onClose={() => setIsFlyoutVisible(false)} aria-labelledby='flyoutTitle'>
          <EuiFlyoutHeader hasBorder>
            <EuiTitle size='m'>
              <h2 id='flyoutTitle'>Complete Domain List</h2>
            </EuiTitle>
          </EuiFlyoutHeader>
          <EuiFlyoutBody>
            <EuiText size='m'>Complete list, arranged alphabetically, of sites where ads may appear. Placements will be optimized for audience match.</EuiText>

            <EuiSpacer size='l' />

            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {sortedDomains.map((site, index) => (
                <ListItem domain={site} index={index} key={index} showNumbers={false} />
              ))}
            </ol>
          </EuiFlyoutBody>
        </AcFlyout>
      )}
    </EuiPanel>
  )
}

export default AIPlanTopInternetDisplaySites
